import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';

export default function AuthPage() {
  const { t } = useTranslation();
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ email: '', password: '', name: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success(t('common.success'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(signupData.email, signupData.password, signupData.name);
      toast.success(t('common.success'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[hsl(var(--background))]">
      {/* Left Side - Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="hero-spotlight absolute inset-0" />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(46,79%,56%)] to-[hsl(174,72%,38%)] flex items-center justify-center font-bold text-black text-xl">
              T
            </div>
            <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TekCep</span>
          </div>
          <h1 className="text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t('auth.welcome')}
          </h1>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-md">
            {t('auth.welcome.desc')}
          </p>
          <div className="mt-12 space-y-4">
            {['Instagram', 'YouTube', 'X (Twitter)', 'Facebook', 'WhatsApp', 'Telegram'].map((ch, i) => (
              <div key={ch} className="flex items-center gap-3 text-sm text-[hsl(var(--muted-foreground))]" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="w-2 h-2 rounded-full bg-[hsl(var(--accent))]" />
                {ch}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(46,79%,56%)] to-[hsl(174,72%,38%)] flex items-center justify-center font-bold text-black text-lg">T</div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>TekCep</span>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-[hsl(var(--secondary))]">
              <TabsTrigger value="login" data-testid="auth-tab-login">{t('auth.login.title')}</TabsTrigger>
              <TabsTrigger value="signup" data-testid="auth-tab-signup">{t('auth.signup.title')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-[hsl(var(--muted-foreground))]">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      type="email"
                      value={loginData.email}
                      onChange={e => setLoginData({ ...loginData, email: e.target.value })}
                      className="pl-10 bg-black/20 border-white/10"
                      placeholder="email@example.com"
                      data-testid="login-email"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[hsl(var(--muted-foreground))]">{t('auth.password')}</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      type="password"
                      value={loginData.password}
                      onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                      className="pl-10 bg-black/20 border-white/10"
                      placeholder="••••••••"
                      data-testid="login-password"
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105" disabled={loading} data-testid="login-submit">
                  {loading ? t('common.loading') : t('auth.login.btn')}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-[hsl(var(--muted-foreground))]">{t('auth.name')}</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      type="text"
                      value={signupData.name}
                      onChange={e => setSignupData({ ...signupData, name: e.target.value })}
                      className="pl-10 bg-black/20 border-white/10"
                      placeholder="John Doe"
                      data-testid="signup-name"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[hsl(var(--muted-foreground))]">{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      type="email"
                      value={signupData.email}
                      onChange={e => setSignupData({ ...signupData, email: e.target.value })}
                      className="pl-10 bg-black/20 border-white/10"
                      placeholder="email@example.com"
                      data-testid="signup-email"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-[hsl(var(--muted-foreground))]">{t('auth.password')}</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      type="password"
                      value={signupData.password}
                      onChange={e => setSignupData({ ...signupData, password: e.target.value })}
                      className="pl-10 bg-black/20 border-white/10"
                      placeholder="••••••••"
                      data-testid="signup-password"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105" disabled={loading} data-testid="signup-submit">
                  {loading ? t('common.loading') : t('auth.signup.btn')}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              ← {t('common.back')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
